export type LoadNotStarted = { status: 'not-started'; data: null; error: null };
export type LoadSuccess<T> = { status: 'success'; data: T; error: null };
export type Loading<T = null> = { status: 'loading'; data: T | null; error: null };
export type LoadError<T = null> = { status: 'error'; data: T | null; error: Error };

export type StreamLoadable<TData> = LoadNotStarted | Loading<TData> | LoadSuccess<TData> | LoadError<TData>;
export type DataLoadable<TData> = LoadNotStarted | Loading | LoadSuccess<TData> | LoadError;
export type Loadable<TData> = StreamLoadable<TData> | DataLoadable<TData>;

export const notStarted = (): LoadNotStarted => ({ status: 'not-started', data: null, error: null });
export const loadSuccess = <T>(data: T): LoadSuccess<T> => ({
  status: 'success',
  data,
  error: null
});
export const loading = <T = null>(data: T | null = null): Loading<T> => ({ status: 'loading', data, error: null });
export const loadError = <T = null>(error: Error, data: T | null = null): LoadError<T> => ({
  status: 'error',
  data,
  error
});

export const isNotStarted = (loadable: Loadable<any>): loadable is LoadNotStarted => loadable.status === 'not-started';
export const isLoadSuccess = <T>(loadable: Loadable<T>): loadable is LoadSuccess<T> => loadable.status === 'success';
export const isLoading = (loadable: Loadable<any>): loadable is Loading<any> => loadable.status === 'loading';
export const isLoadError = (loadable: Loadable<any>): loadable is LoadError<any> => loadable.status === 'error';

export const getLoadableData = <T>(loadable: Loadable<T>): Loadable<T>['data'] => loadable.data;
export const getLoadSuccessData = <T>(loadable: LoadSuccess<T>): LoadSuccess<T>['data'] => loadable.data;

export const combineLoadable = <TData1, TData2, TResult>(
  firstLoadable: Loadable<TData1>,
  secondLoadable: Loadable<TData2>,
  combineData: (data1: TData1 | null, data2: TData2 | null) => TResult
) => {
  if (isLoadError(firstLoadable))
    return loadError(firstLoadable.error, combineData(firstLoadable.data, secondLoadable.data));
  if (isLoadError(secondLoadable))
    return loadError(secondLoadable.error, combineData(firstLoadable.data, secondLoadable.data));
  if (isLoading(firstLoadable) || isLoading(secondLoadable))
    return loading(combineData(firstLoadable.data, secondLoadable.data));
  if (isNotStarted(firstLoadable) || isNotStarted(secondLoadable)) return notStarted();

  return loadSuccess(combineData(firstLoadable.data, secondLoadable.data));
};
